import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Icon from '../Icon.jsx';
import Modal from '../Modal.jsx';
import PageSkeleton from '../Skeleton.jsx';
import Spin from '../Spin.jsx';

/**
 * Hesap ekranı (Faz 3). Üç şeyi aynı ekranda gösterir:
 *   1. Kalem kalem ödenmiş / BAŞKASININ ELİNDE / açık bakiye
 *   2. Hızlı tahsil: tümü · seçili kalemler · serbest tutar · eşit bölüşme
 *   3. Para izi: ödemeler, iadeler, indirimler, zarar yazmalar
 *
 * Kasa düzeni: ekranın ALTI tek bir düğme tahtası — ödeme türleri ve diğer
 * işlemler yan yana, parmakla basılacak kadar iri. Tutar ya da gerekçe
 * soracak her iş POPUP açar (tarayıcı prompt'u yok): kasiyer sayıyı ekranı
 * terk etmeden girer, yanlışsa vazgeçer. Kalem seçimi için satırın HER YERİ
 * tıklanır; küçük onay kutusuna nişan almaya gerek yok.
 *
 * Tutarlar burada HESAPLANMAZ: seçim sunucuya gider, sunucu kilit altında
 * hesaplar. Panelin gösterdiği her sayı /bill yanıtından gelir.
 *
 * TAHSİLAT BİTİNCE MASAYI KAPATMAYI KENDİMİZ SORAR: bakiye sıfırlandığı anda
 * popup çıkar, "Masayı kapat" bir dokunuşla hesabı kapatıp salona döner.
 * Eskiden kasiyer son kuruşu aldıktan sonra masa OCCUPIED kalıyordu — salon
 * ekranı dolu görünüyor, masaya yeni misafir oturtulmuyordu.
 */

/** "12,50" → 1250. Geçersiz/sıfır ise null döner. */
function toMinor(v) {
  const n = Number(String(v).replace(',', '.').trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

export default function Bill() {
  const tr = useT();
  const nav = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  // Paranın hangi kasaya/cihaza girdiği: liste restoranın kendisi
  // (Ayarlar › Tahsilat hesapları).
  const [accounts, setAccounts] = useState([]);
  // Tek popup durumu:
  // { kind: 'collect'|'discount'|'writeoff'|'close'|'closeAsk'|'split'|'refund', ... }
  const [dlg, setDlg] = useState(null);
  // Kapatma ayrı bir bekleme durumu: `busy` tüm tahtayı kilitler, bu ise
  // basılan düğmenin İÇİNDE halka döndürür ve ikinci basışı engeller.
  const [closing, setClosing] = useState(false);

  // Taze kaydı GERİ DÖNDÜRÜR: tahsilattan sonra "bakiye sıfırlandı mı"
  // sorusunun cevabı state'in güncellenmesini beklemeden okunabilsin.
  const load = () => api(`/api/sessions/${id}/bill`).then((d) => { setData(d); setPicked({}); return d; })
    .catch((e) => { setErr(errMessage(tr, e)); return null; });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api('/api/payments/accounts').then(setAccounts).catch(() => {});
  }, []);

  /**
   * Her kasa işi buradan geçer: hata tek yerde yakalanır, ekran tazelenir ve
   * hesap TAM ödendiyse kapatma popup'ı kendiliğinden açılır.
   *
   * Popup yalnız bakiye VARDIYSA çıkar: zaten sıfır olan bir hesapta indirim
   * ya da rezervasyon bırakma işi yapıldığında "kapatalım mı" diye sormak
   * sorunun kendisini anlamsızlaştırıyor.
   */
  async function act(fn) {
    const dueBefore = data?.totals?.dueMinor ?? 0;
    setErr(''); setMsg(''); setBusy(true);
    try {
      await fn();
      const fresh = await load();
      if (fresh && dueBefore > 0 && fresh.totals.dueMinor === 0
          && fresh.session.status !== 'CLOSED') {
        setDlg({ kind: 'closeAsk' });
      }
      return fresh;
    } catch (e) {
      setErr(errMessage(tr, e));
      return null;
    } finally { setBusy(false); }
  }

  /**
   * Masayı kapat. Başarıda SALONA döner: hesap kapandıktan sonra bu ekranda
   * yapılacak iş kalmıyor, garsonun sıradaki işi masa ızgarasında.
   * `closing` yalnız hatada sıfırlanır — başarıda sayfa değişiyor.
   */
  const closeTable = async (reason) => {
    setErr(''); setMsg(''); setClosing(true);
    try {
      await api(`/api/sessions/${id}/close`, { method: 'POST', body: { reason } });
      nav('/tables');
    } catch (e) {
      setErr(errMessage(tr, e));
      setClosing(false);
    }
  };

  if (err && !data) return <p className="err">{err}</p>;
  if (!data) return <PageSkeleton shape="bill" />;

  const cur = data.session.currency;
  const pickedItems = data.items.filter((i) => picked[i.id] && i.availableMinor > 0);
  const pickedTotal = pickedItems.reduce((a, i) => a + i.availableMinor, 0);
  const t = data.totals;
  const closed = data.session.status === 'CLOSED';

  const togglePick = (i) => {
    if (i.availableMinor === 0) return;
    setPicked((p) => ({ ...p, [i.id]: !p[i.id] }));
  };

  /**
   * Nakit/kart tahsil. Seçili kalemler varsa önce REZERVASYON açılır (kilit),
   * tutarı sunucu söyler; serbest tutarda doğrudan ödeme yazılır.
   */
  const collect = ({ account, amountMinor, usePicked, tipMinor }) => act(async () => {
    let reservationId;
    let amount = amountMinor;
    if (usePicked) {
      const reservation = await api(`/api/sessions/${id}/reservations`, {
        method: 'POST',
        body: { kind: 'ITEMS', lines: pickedItems.map((i) => ({ orderItemId: i.id })) },
      });
      reservationId = reservation.id;
      amount = reservation.amountMinor;
    }
    const out = await api(`/api/sessions/${id}/payments/cash`, {
      method: 'POST',
      body: {
        amountMinor: amount, reservationId, accountId: account.id,
        ...(tipMinor > 0 ? { tipMinor } : {}),
        clientUuid: crypto.randomUUID(),
      },
    });
    setMsg(out.leftoverMinor > 0
      ? tr('bl.collectedOver', { amount: money(out.leftoverMinor, cur) })
      : tr('bl.collectedAmount', { amount: money(amount, cur) }));
  });

  return (
    <>
      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>
            {tr('bl.title', { table: data.session.table?.name || '—', code: data.session.code })}
          </h2>
          <span className={`badge ${closed ? '' : 'ok'}`}>{data.session.status}</span>
          <span className="spacer" />
          {/* İki düğme birlikte kalsın: dar ekranda biri alt satıra düşünce
              başlık satırı üç kata çıkıyordu. */}
          <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
            <Link to="/tables"><button><Icon n="grid" />{tr('nav.tables')}</button></Link>
            <button onClick={load} disabled={busy}><Icon n="refresh" />{tr('refresh')}</button>
          </div>
        </div>
        <div className="row" style={{ marginTop: 10, gap: 18 }}>
          <span>{tr('total')} <b>{money(t.totalMinor, cur)}</b></span>
          <span>{tr('paidLabel')} <b>{money(t.paidMinor, cur)}</b></span>
          {t.heldMinor > 0 && <span className="muted">{tr('bl.held')} <b>{money(t.heldMinor, cur)}</b></span>}
          <span>{tr('dueLabel')} <b style={{ color: t.dueMinor > 0 ? 'var(--warn)' : 'var(--ok)' }}>{money(t.dueMinor, cur)}</b></span>
          {data.session.tipMinor > 0 && <span className="muted">{tr('tip')} {money(data.session.tipMinor, cur)}</span>}
          <span className="muted">{tr('bl.vatIncluded', { amount: money(data.session.taxMinor, cur) })}</span>
        </div>
      </div>

      <div className="card">
        <h2>{tr('bl.items')}</h2>
        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }}></th>
              <th>{tr('menu.item')}</th>
              <th className="num">{tr('amount')}</th>
              {/* İkincil sütunlar: dar ekranda gizlenir, "açık" tutar zaten
                  kararı veriyor (styles.css → .col-sec). */}
              <th className="col-sec">{tr('paidLabel')}</th>
              <th className="col-sec">{tr('bl.heldCol')}</th>
              <th className="num">{tr('bl.openCol')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((i) => {
              const off = i.availableMinor === 0;
              return (
                // Satırın tamamı seçim hedefi: kasada parmakla 18px'lik kutuya
                // nişan almak yavaş. Klavyede de Enter/Boşluk ile seçilir.
                <tr key={i.id} className={`pickrow${off ? ' off' : ''}${picked[i.id] ? ' on' : ''}`}
                  style={{ opacity: off ? 0.6 : 1 }}
                  tabIndex={off ? -1 : 0} aria-selected={!!picked[i.id]}
                  onClick={() => togglePick(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePick(i); }
                  }}>
                  {/* Kutunun kendi tıklaması satıra ULAŞMASIN: ikisi birden
                      çalışınca seçim anında geri alınıyordu. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" disabled={off} checked={!!picked[i.id]}
                      tabIndex={-1} onChange={() => togglePick(i)} />
                  </td>
                  <td>{i.qty}× {i.nameSnapshot}</td>
                  <td className="num">{money(i.lineTotalMinor, cur)}</td>
                  <td className="col-sec">{i.paidMinor ? money(i.paidMinor, cur) : '—'}</td>
                  <td className="col-sec">{i.heldMinor ? <span className="badge warn">{money(i.heldMinor, cur)}</span> : '—'}</td>
                  <td className="num"><b>{money(i.availableMinor, cur)}</b></td>
                </tr>
              );
            })}
            {data.items.length === 0 && <tr><td colSpan="6" className="muted">{tr('bl.noItems')}</td></tr>}
          </tbody>
        </table>
        <p className="muted" style={{ marginBottom: 0 }}>{tr('bl.pickHint')}</p>
      </div>

      {/* ── Kasa tahtası ──────────────────────────────────────────────────
          Ödeme türleri önce (günün işi bu), diğer işlemler altında. Hepsi
          aynı boyda ve yan yana; basınca tutarı/gerekçeyi popup sorar. */}
      <div className="card">
        <h2>{tr('bl.collect')}</h2>
        <p className="muted" style={{ marginTop: -4 }}>
          {pickedTotal > 0
            ? tr('bl.pickedHint', { n: pickedItems.length, amount: money(pickedTotal, cur) })
            : tr('bl.collectHint')}
        </p>

        <div className="paybtns">
          {accounts.map((a) => (
            <button key={a.id} className="paybtn pay" disabled={busy || closed || t.dueMinor === 0}
              onClick={() => setDlg({ kind: 'collect', account: a })}>
              <span className="pb-h"><Icon n={a.icon} />{a.name}</span>
              <em>{money(pickedTotal > 0 ? pickedTotal : t.dueMinor, cur)}</em>
            </button>
          ))}
        </div>

        <h3 className="sub">{tr('bl.otherActions')}</h3>
        <div className="paybtns">
          <button className="paybtn" disabled={busy || t.dueMinor === 0}
            onClick={() => setDlg({ kind: 'discount' })}>
            <span className="pb-h"><Icon n="percent" />{tr('bl.applyDiscount')}</span>
          </button>
          <button className="paybtn" disabled={busy} onClick={() => setDlg({ kind: 'split' })}>
            <span className="pb-h"><Icon n="users" />{tr('bl.computeShares')}</span>
          </button>
          <button className="paybtn" disabled={busy || t.dueMinor === 0}
            onClick={() => setDlg({ kind: 'writeoff' })}>
            <span className="pb-h"><Icon n="ban" />{tr('bl.writeOff')}</span>
          </button>
          <button className="paybtn" disabled={busy || closed} onClick={() => setDlg({ kind: 'close' })}>
            <span className="pb-h"><Icon n="checkCircle" />{tr('tbl.closeBill')}</span>
          </button>
        </div>

        {(data.discounts.length > 0 || data.adjustments.length > 0) && (
          <ul className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            {data.discounts.map((d) => (
              <li key={`d${d.id}`}>{tr('bl.discountLine', { amount: money(d.amountMinor, cur), kind: d.kind })}{d.reason ? ` · ${d.reason}` : ''}</li>
            ))}
            {data.adjustments.map((a) => (
              <li key={`a${a.id}`}>{tr('bl.writeOffLine', { amount: money(a.amountMinor, cur), reason: a.reason })}</li>
            ))}
          </ul>
        )}
        <p className="muted" style={{ marginBottom: 0 }}>{tr('bl.cashNote')}</p>
      </div>

      <div className="card">
        <h2>{tr('bl.payments')}</h2>
        <table>
          <thead>
            <tr><th>#</th><th>{tr('bl.method')}</th><th>{tr('status')}</th><th className="num">{tr('amount')}</th><th className="col-sec">{tr('tip')}</th><th className="col-sec">{tr('bl.refund')}</th><th className="col-sec">{tr('bl.commission')}</th><th></th></tr>
          </thead>
          <tbody>
            {data.payments.map((p) => {
              const refundable = Math.max(0, p.grossMinor - p.tipMinor - p.refundedMinor);
              return (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  {/* TEST ödemesi gerçeğinden ayrılsın: hesap kapanmış
                      görünse de kasaya para girmediği okunabilmeli. */}
                  <td>{p.method}{p.demo && <> <span className="badge warn">{tr('bl.demoBadge')}</span></>}</td>
                  <td><span className={`badge ${p.status === 'SUCCEEDED' ? 'ok' : p.status.includes('REFUND') ? 'warn' : ''}`}>{p.status}</span></td>
                  <td className="num">{money(p.grossMinor, cur)}</td>
                  <td className="col-sec">{p.tipMinor ? money(p.tipMinor, cur) : '—'}</td>
                  <td className="col-sec">{p.refundedMinor ? money(p.refundedMinor, cur) : '—'}</td>
                  <td className="col-sec">{p.applicationFeeMinor ? money(p.applicationFeeMinor, cur) : '—'}</td>
                  <td>
                    <button className="btn-sm" disabled={busy || refundable === 0}
                      onClick={() => setDlg({ kind: 'refund', payment: p, max: refundable })}>
                      <Icon n="undo" />{tr('bl.refund')}
                    </button>
                  </td>
                </tr>
              );
            })}
            {data.payments.length === 0 && <tr><td colSpan="8" className="muted">{tr('bl.noPayments')}</td></tr>}
          </tbody>
        </table>
        <p className="muted" style={{ marginBottom: 0 }}>{tr('bl.refundNote')}</p>
      </div>

      {data.reservations.length > 0 && (
        <div className="card">
          <h2>{tr('bl.openReservations')}</h2>
          <ul className="muted" style={{ fontSize: 13 }}>
            {data.reservations.map((r) => (
              <li key={r.id}>
                #{r.id} · {r.kind} · {money(r.amountMinor, cur)} ·{' '}
                {r.guestDeviceId ? tr('bl.guestPhone') : tr('bl.register')} ·{' '}
                {tr('bl.secondsLeft', { n: Math.max(0, Math.round((new Date(r.expiresAt) - Date.now()) / 1000)) })}
                <button style={{ marginLeft: 8, padding: '0 6px' }}
                  onClick={() => act(() => api(`/api/sessions/${id}/reservations/${r.id}`, { method: 'DELETE' }))}>
                  <Icon n="x" />{tr('bl.release')}
                </button>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ marginBottom: 0 }}>{tr('bl.releaseNote')}</p>
        </div>
      )}

      {dlg?.kind === 'collect' && (
        <CollectDialog tr={tr} cur={cur} account={dlg.account} dueMinor={t.dueMinor}
          pickedTotal={pickedTotal} pickedCount={pickedItems.length}
          onClose={() => setDlg(null)}
          onSubmit={(v) => { setDlg(null); collect({ account: dlg.account, ...v }); }} />
      )}

      {dlg?.kind === 'discount' && (
        <AskDialog tr={tr} icon="percent" title={tr('bl.applyDiscount')}
          subtitle={tr('bl.discountPrompt', { max: money(t.dueMinor, cur) })}
          amount={{ required: true, initial: '' }}
          reason={{ required: false, placeholder: tr('bl.discountReasonPrompt') }}
          submitLabel={tr('bl.applyDiscount')}
          onClose={() => setDlg(null)}
          onSubmit={({ amountMinor, reason }) => {
            setDlg(null);
            act(() => api(`/api/sessions/${id}/discounts`, { method: 'POST', body: { amountMinor, reason } }));
          }} />
      )}

      {dlg?.kind === 'writeoff' && (
        <AskDialog tr={tr} icon="ban" title={tr('bl.writeOff')}
          subtitle={tr('bl.writeOffPrompt', { amount: money(t.dueMinor, cur) })}
          reason={{ required: true }}
          submitLabel={tr('bl.writeOff')}
          onClose={() => setDlg(null)}
          onSubmit={({ reason }) => {
            setDlg(null);
            act(() => api(`/api/sessions/${id}/adjustments`, { method: 'POST', body: { reason } }));
          }} />
      )}

      {dlg?.kind === 'close' && (
        <AskDialog tr={tr} icon="checkCircle" title={tr('tbl.closeBill')}
          subtitle={t.dueMinor > 0
            ? tr('tbl.closeReasonPrompt', { amount: money(t.dueMinor, cur) })
            : tr('bl.closeNoDue')}
          reason={t.dueMinor > 0 ? { required: true } : null}
          submitLabel={tr('tbl.closeBill')}
          busy={closing}
          onClose={() => { if (!closing) setDlg(null); }}
          onSubmit={({ reason }) => closeTable(reason)} />
      )}

      {/* Tahsilat tamamlandı → tek dokunuşla kapat. Perde ve "Şimdi değil"
          kapatmayı ATLAMAYI da kolay tutar: fiş yazdırmak, ikram bir kahve
          ya da ikinci tur hâlâ mümkün. */}
      {dlg?.kind === 'closeAsk' && (
        <Modal title={tr('bl.paidAllTitle')} subtitle={tr('bl.paidAllBody')}
          onClose={() => { if (!closing) setDlg(null); }}
          footer={
            <div className="row">
              <button disabled={closing} onClick={() => setDlg(null)}>
                <Icon n="x" />{tr('bl.notNow')}
              </button>
              <span className="spacer" />
              <button className="primary" disabled={closing} onClick={() => closeTable(null)}>
                {closing ? <Spin /> : <Icon n="checkCircle" />}
                {closing ? tr('tbl.closing') : tr('bl.closeTable')}
              </button>
            </div>
          }>
          <p className="muted" style={{ marginBottom: 0 }}>{tr('bl.paidAllHint')}</p>
        </Modal>
      )}

      {dlg?.kind === 'refund' && (
        <AskDialog tr={tr} icon="undo" title={`${tr('bl.refund')} · #${dlg.payment.id}`}
          subtitle={tr('bl.refundPrompt', { max: money(dlg.max, cur) })}
          amount={{ required: false, initial: (dlg.max / 100).toFixed(2) }}
          reason={{ required: false, placeholder: tr('bl.refundReasonPrompt') }}
          submitLabel={tr('bl.refund')}
          onClose={() => setDlg(null)}
          onSubmit={({ amountMinor, reason }) => {
            setDlg(null);
            const body = { reason };
            if (amountMinor) body.amountMinor = amountMinor;
            act(() => api(`/api/payments/${dlg.payment.id}/refunds`, { method: 'POST', body }));
          }} />
      )}

      {dlg?.kind === 'split' && (
        <SplitDialog tr={tr} cur={cur} id={id} onClose={() => setDlg(null)} />
      )}

      {msg && <p className="ok">{msg}</p>}
      {err && <p className="err">{err}</p>}
    </>
  );
}

/**
 * Tahsil popup'ı: NE KADAR sorusunun tek yeri. Açılışta hazır cevap gelir
 * (seçili kalem varsa onun tutarı, yoksa kalan hesap) — kasiyer çoğu zaman
 * sadece onaylar. Rakama dokunduğu anda "seçili kalem" kilidi bırakılır,
 * çünkü artık serbest tutar tahsil ediliyordur.
 */
function CollectDialog({ tr, cur, account, dueMinor, pickedTotal, pickedCount, onClose, onSubmit }) {
  const [usePicked, setUsePicked] = useState(pickedTotal > 0);
  const [amt, setAmt] = useState((((pickedTotal > 0 ? pickedTotal : dueMinor) || 0) / 100).toFixed(2));
  const [tip, setTip] = useState('');
  const [e, setE] = useState('');
  const box = useRef(null);

  // Modal açılışta kapatma düğmesine odaklanıyor; tutar kutusu daha işe yarar.
  useEffect(() => {
    const h = requestAnimationFrame(() => box.current?.select());
    return () => cancelAnimationFrame(h);
  }, []);

  const setFree = (v) => { setAmt(v); setUsePicked(false); };
  const shownMinor = usePicked ? pickedTotal : (toMinor(amt) || 0);

  const submit = () => {
    const tipMinor = tip.trim() ? toMinor(tip) : 0;
    if (tip.trim() && tipMinor == null) return setE(tr('err.invalid_amount'));
    if (usePicked) return onSubmit({ usePicked: true, tipMinor });
    const m = toMinor(amt);
    if (m == null) return setE(tr('err.invalid_amount'));
    onSubmit({ amountMinor: m, tipMinor });
  };

  return (
    <Modal title={tr('bl.collectWith', { account: account.name })}
      subtitle={tr('bl.dueNow', { amount: money(dueMinor, cur) })} onClose={onClose}
      footer={
        <div className="row">
          <button onClick={onClose}><Icon n="x" />{tr('cancel')}</button>
          <span className="spacer" />
          <button className="primary" disabled={shownMinor <= 0} onClick={submit}>
            <Icon n={account.icon} />{tr('bl.doCollect', { amount: money(shownMinor, cur) })}
          </button>
        </div>
      }>
      <div className="row chiprow" style={{ marginBottom: 12 }}>
        <button className={!usePicked && toMinor(amt) === dueMinor ? 'chip on' : 'chip'}
          onClick={() => setFree((dueMinor / 100).toFixed(2))}>
          {tr('bl.quickAll', { amount: money(dueMinor, cur) })}
        </button>
        {pickedTotal > 0 && (
          <button className={usePicked ? 'chip on' : 'chip'}
            onClick={() => { setUsePicked(true); setAmt((pickedTotal / 100).toFixed(2)); }}>
            {tr('bl.quickPicked', { amount: money(pickedTotal, cur) })}
          </button>
        )}
      </div>

      <div className="grid2">
        <div>
          <label>{tr('amount')}</label>
          <input ref={box} inputMode="decimal" value={amt} onChange={(ev) => setFree(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') submit(); }} />
        </div>
        <div>
          <label>{tr('tip')}</label>
          <input inputMode="decimal" value={tip} placeholder={tr('bl.optional')}
            onChange={(ev) => setTip(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') submit(); }} />
        </div>
      </div>

      {usePicked && (
        <p className="muted" style={{ marginBottom: 0 }}>
          {tr('bl.pickedHint', { n: pickedCount, amount: money(pickedTotal, cur) })}
        </p>
      )}
      {e && <p className="err">{e}</p>}
    </Modal>
  );
}

/**
 * Tutar ve/veya gerekçe soran genel popup (indirim, zarar yazma, kapatma,
 * iade). Zorunlu alan boşken düğme kapalı kalır — uyarı yazısı yerine
 * düğmenin kendisi anlatır.
 */
function AskDialog({ tr, icon, title, subtitle, amount, reason, submitLabel, busy, onClose, onSubmit }) {
  const [amt, setAmt] = useState(amount?.initial || '');
  const [why, setWhy] = useState('');
  const [e, setE] = useState('');
  const first = useRef(null);

  useEffect(() => {
    const h = requestAnimationFrame(() => first.current?.focus());
    return () => cancelAnimationFrame(h);
  }, []);

  const ready = !busy
    && (!amount?.required || amt.trim() !== '')
    && (!reason?.required || why.trim() !== '');

  const submit = () => {
    if (!ready) return;
    const out = { reason: why.trim() || null };
    if (amount && (amount.required || amt.trim() !== '')) {
      const m = toMinor(amt);
      if (m == null) return setE(tr('err.invalid_amount'));
      out.amountMinor = m;
    }
    onSubmit(out);
  };

  return (
    <Modal title={title} subtitle={subtitle} onClose={onClose}
      footer={
        <div className="row">
          <button disabled={busy} onClick={onClose}><Icon n="x" />{tr('cancel')}</button>
          <span className="spacer" />
          <button className="primary" disabled={!ready} onClick={submit}>
            {busy ? <Spin /> : <Icon n={icon} />}{submitLabel}
          </button>
        </div>
      }>
      <div className="grid2">
        {amount && (
          <div>
            <label>{tr('amount')}</label>
            <input ref={first} inputMode="decimal" value={amt}
              placeholder={amount.required ? '' : tr('bl.optional')}
              onChange={(ev) => setAmt(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') submit(); }} />
          </div>
        )}
        {reason && (
          <div>
            <label>{tr('reason')}</label>
            <input ref={amount ? undefined : first} value={why}
              placeholder={reason.required ? (reason.placeholder || '') : tr('bl.optional')}
              onChange={(ev) => setWhy(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') submit(); }} />
          </div>
        )}
      </div>
      {e && <p className="err">{e}</p>}
    </Modal>
  );
}

/** Eşit bölüşme popup'ı: kişi sayısını değiştir, payları anında gör. */
function SplitDialog({ tr, cur, id, onClose }) {
  const [parts, setParts] = useState(2);
  const [out, setOut] = useState(null);
  const [e, setE] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (n) => {
    setBusy(true); setE('');
    try { setOut(await api(`/api/sessions/${id}/split/equal?parts=${n}`)); }
    catch (x) { setE(errMessage(tr, x)); setOut(null); }
    finally { setBusy(false); }
  };
  useEffect(() => { run(2); /* eslint-disable-next-line */ }, []);

  return (
    <Modal title={tr('bl.computeShares')} onClose={onClose}
      footer={<div className="row"><span className="spacer" /><button onClick={onClose}><Icon n="check" />{tr('close')}</button></div>}>
      <div className="row">
        <label style={{ margin: 0 }}>{tr('bl.splitParts')}</label>
        <input type="number" min="2" max="50" style={{ maxWidth: 90 }} value={parts}
          onChange={(ev) => setParts(Number(ev.target.value))} />
        <button disabled={busy || !(parts >= 2)} onClick={() => run(parts)}>
          <Icon n="users" />{tr('bl.computeShares')}
        </button>
      </div>
      {out && (
        <p className="muted" style={{ marginBottom: 0 }}>
          {tr('bl.sharesLine', {
            parts: out.parts,
            list: out.shares.map((s) => money(s, cur)).join(' + '),
            sum: money(out.sum, cur),
          })}
        </p>
      )}
      {e && <p className="err">{e}</p>}
    </Modal>
  );
}
